import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { regraFiscalService } from "@/features/fiscal/services/fiscalService";
import type { RegraFiscalListagemDto } from "@/features/fiscal/types";

interface Props {
  value?:       number | null;   // id da regra fiscal
  onChange:     (id: number) => void;
  placeholder?: string;
  disabled?:    boolean;
}

export const rotuloRegraFiscal = (r: RegraFiscalListagemDto) =>
  `NCM ${r.ncmCodigo ?? `#${r.ncmId}`} · CFOP ${r.cfopCodigo ?? `#${r.cfopId}`}${r.ufDestino ? ` · ${r.ufDestino}` : ""}`;

/** Rótulo "NCM · CFOP · UF" de uma regra pelo id (listagens que só guardam o id). */
export function RegraFiscalRotulo({ id }: { id?: number | null }) {
  const { data: regra, isLoading } = useQuery({
    queryKey: ["regra-fiscal-selected", id],
    queryFn:  () => regraFiscalService.listar({ id: id ?? undefined, pagina: 1, tamanhoPagina: 1 }),
    enabled:  !!id,
    staleTime: 60_000,
    select:   (r) => r.itens[0],
  });
  if (!id) return <>—</>;
  if (isLoading) return <span className="text-muted-foreground">…</span>;
  return <>{regra ? rotuloRegraFiscal(regra) : "Regra removida"}</>;
}

/** Regra Fiscal com busca no servidor por código de NCM/CFOP (sem o teto de 200 da lista fixa). */
export function RegraFiscalSearchCombobox({
  value,
  onChange,
  placeholder = "Buscar regra por NCM ou CFOP...",
  disabled,
}: Props) {
  const [open, setOpen]           = useState(false);
  const [search, setSearch]       = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["regra-fiscal-search", debounced],
    queryFn:  () => regraFiscalService.listar({ busca: debounced || undefined, pagina: 1, tamanhoPagina: 50 }),
    enabled:  open,
    staleTime: 30_000,
  });
  const results = data?.itens ?? [];

  const { data: selecionado } = useQuery({
    queryKey: ["regra-fiscal-selected", value],
    queryFn:  () => regraFiscalService.listar({ id: value ?? undefined, pagina: 1, tamanhoPagina: 1 }),
    enabled:  !!value,
    staleTime: 60_000,
    select:   (r) => r.itens[0],
  });

  const label = selecionado ? rotuloRegraFiscal(selecionado) : value ? `#${value}` : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          type="button"
        >
          <span className={cn("truncate text-left", !value && "text-muted-foreground")}>{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite o código do NCM ou do CFOP..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && <CommandEmpty>Nenhuma regra encontrada.</CommandEmpty>}
            <CommandGroup>
              {results.map(r => (
                <CommandItem
                  key={r.id}
                  value={String(r.id)}
                  onSelect={() => { onChange(r.id); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === r.id ? "opacity-100" : "opacity-0")} />
                  <span className="text-sm">{rotuloRegraFiscal(r)}</span>
                  {!r.isAtivo && <span className="ml-2 text-xs text-muted-foreground">(inativa)</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
